
  import { defineComponent, PropType } from 'vue';
  import { Cell, createColumnHelper } from '@tanstack/vue-table';

  import TableAggregated from '@/common/components/redesigned/Table/TableAggregated.vue';
  import {
    TPaginationConfig,
    TTableAggregatedProgressBarOptions,
    TTableWithOuterFilterConfig,
  } from '@/common/components/redesigned/Table/types/tableTypes';
  import text from '@/common/mixins/text';
  import { TStorePaths } from '@/common/types/storePaths';

  import { PAGINATION_CONFIG } from '../../constants/pagination';
  import { TABLE_AGGREGATED_PROGRESS_BAR_OPTIONS } from '../../constants/tableAggregatedProgress';
  import { TRegionAggregatedItem, TRegionAggregatedTableData } from '../../types/region-aggregated';

  export default defineComponent({
    name: 'RegionAggregatedTable',
    components: {
      TableAggregated,
    },
    mixins: [text],
    props: {
      filter: {
        type: Object as PropType<TTableWithOuterFilterConfig['params']>,
        required: true,
      },
      storePaths: {
        type: Object as PropType<TStorePaths>,
        required: true,
      },
    },
    computed: {
      getPaginationConfig(): TPaginationConfig {
        return PAGINATION_CONFIG;
      },
      getProgressBarOptions(): TTableAggregatedProgressBarOptions {
        return TABLE_AGGREGATED_PROGRESS_BAR_OPTIONS;
      },
    },
    methods: {
      generateColumns(data: TRegionAggregatedTableData) {
        if (!data) return [];

        const columnHelper = createColumnHelper<TRegionAggregatedItem>();

        return [
          columnHelper.accessor('name', {
            header: 'Наименование субъекта РФ',
            size: 400,
            meta: {
              fixed: true,
            },
          }),
          ...(data.periods ?? []).map((period, index) => {
            return columnHelper.group({
              header: period,
              id: `${period}${index}`,
              columns: [
                columnHelper.group({
                  header: 'Экспорт',
                  columns: [
                    columnHelper.accessor(
                      (row: TRegionAggregatedItem) => this.formatNumber(row.indicators[index]?.export.weight, 1),
                      {
                        header: 'Вес, тонн',
                        id: `export-weight-${index}`,
                        size: 160,
                      },
                    ),
                    columnHelper.accessor(
                      (row: TRegionAggregatedItem) => this.formatNumber(row.indicators[index]?.export.price, 1),
                      {
                        header: 'Стоимость, тыс. долл.',
                        id: `export-price-${index}`,
                        size: 160,
                      },
                    ),
                  ],
                }),
                columnHelper.group({
                  header: 'Импорт',
                  columns: [
                    columnHelper.accessor(
                      (row: TRegionAggregatedItem) => this.formatNumber(row.indicators[index]?.import.weight, 1),
                      {
                        header: 'Вес, тонн',
                        id: `import-weight-${index}`,
                        size: 160,
                      },
                    ),
                    columnHelper.accessor(
                      (row: TRegionAggregatedItem) => this.formatNumber(row.indicators[index]?.import.price, 1),
                      {
                        header: 'Стоимость, тыс. долл.',
                        id: `import-price-${index}`,
                        size: 160,
                      },
                    ),
                  ],
                }),
              ],
            });
          }),
        ];
      },
      rowClassGetter(row: TRegionAggregatedItem) {
        return `region-aggregated-table__row--level-${row.level}`;
      },
      cellClassGetter(cell: Cell<TRegionAggregatedItem, any>) {
        return cell.id.includes('name')
          ? `region-aggregated-table__cell--name region-aggregated-table__cell--name-${cell.row.original.level}`
          : '';
      },
    },
  });
